import {comparableActions} from 'lib/ComparablesActionsUtil';
import propertyDetailActions from 'actionCreators/propertyDetailActions';
import {UPDATE as PROPERTYDETAIL_UPDATE} from 'ducks/propertyDetail';
import ClapiApi from 'api/ClapiApi';
import UserAssetsApi from 'api/UserAssetsApi';
import {compact} from 'lodash/array';
import {PHOTO_GALLERY_FIELDS, PROPERTY_AVM_FIELDS, PROPERTY_HISTORY_FIELDS} from 'lib/Commons';
import PropertyDetailImages from 'lib/PropertyDetailImages';
import PlaceholderImage from 'images/no-image@2x.png';
import {setPropertyNotes} from 'ducks/propertyNotes';
import PropertyDetailsApi from 'api/PropertyDetailsApi';
import {toggleFloorPlan} from 'ducks/reportSections';

const defaultAdDescription = 'No listing description available.';

export function fetchAdvertisement({propertyId, comparableType}) {
  const {actions} = comparableActions[comparableType];
  return async (dispatch) => {
    await dispatch(actions.updateFetching({propertyId, fetching: {advertisementDescription: true}}));
    let advertisementDescription = await ClapiApi.getAdvertisementDescription(propertyId, comparableType);
    if (!advertisementDescription) {
      advertisementDescription = defaultAdDescription;
    }
    const payload = {
      propertyId,
      advertisementDescription,
    };
    await dispatch({type: actions.RESET_COMPARABLE, payload});
    await dispatch(actions.updateFetching({propertyId, fetching: {advertisementDescription: false}}));
    return advertisementDescription;
  };
}

export function fetchComparablePropertyPhotos(
  {
    address,
    comparableType,
    isComparable,
    latitude,
    longitude,
    propertyId,
  }
) {
  const {actions} = !!isComparable ? comparableActions[comparableType] : {actions: propertyDetailActions};
  const resetActionType = isComparable ? actions.RESET_COMPARABLE : PROPERTYDETAIL_UPDATE;
  return async (dispatch, getState) => {
    // Create maps of fetching flags
    const fetchingStartState = PHOTO_GALLERY_FIELDS.reduce((acc, field) => ({...acc, [field]: true}), {});
    const fetchingEndState = PHOTO_GALLERY_FIELDS.reduce((acc, field) => ({...acc, [field]: false}), {});
    const {reportType: {value: reportType}} = getState();

    // Prepare the payload to update the property's state
    let propertyUpdatePayload = {
      propertyId,
    };
    await dispatch(actions.updateFetching({propertyId, fetching: fetchingStartState}));

    // Property photo API promise, update the fetching upon completion so the rest of the app can asynchronously update
    const propertyPhotosPromise = ClapiApi.getClPropertyPhotos(propertyId)
      .then(res => {
        propertyUpdatePayload.floorPlanUris = res.floorPlanUris;
        propertyUpdatePayload.propertyPhotoList = res.propertyPhotoList;
        if (!isComparable) {
          const {propertyDetail} = getState();
          propertyUpdatePayload = {
            ...propertyUpdatePayload,
            ...prepareTargetPropertyImages(res, propertyDetail),
          };
        }
        return (res.propertyPhotoList);
      })
      .finally(() => (dispatch(actions.updateFetching({
        propertyId,
        fetching: {
          floorPlanUris: false,
          secondaryImageUris: false,
          selectedImages: false,
          targetPhotoList: false,
        }
      }))));

    const aerialMapViewUriPromise = ClapiApi.getAerialMapPhoto({address, latitude, longitude})
      .then(url => {
        propertyUpdatePayload.aerialMapViewUri = url;
        return [url];
      })
      .finally(() => (dispatch(actions.updateFetching({propertyId, fetching: {aerialViewImageUri: false}}))));

    const streetMapViewUriPromise = ClapiApi.getStreetMapPhoto({address, latitude, longitude})
      .then(url => {
        propertyUpdatePayload.streetViewImageUri = url;
        return [url];
      })
      .finally(() => (dispatch(actions.updateFetching({propertyId, fetching: {streetMapViewUri: false}}))));

    const userPhotosPromise = UserAssetsApi.getUserPhotos(propertyId)
      .then(uploadPhotoList => {
        propertyUpdatePayload.uploadPhotoList = uploadPhotoList;
        return uploadPhotoList;
      })
      .finally(() => (dispatch(actions.updateFetching({propertyId, fetching: {uploadPhotoList: false}}))));

    // Wait for all Promises to be either resolved, rejected or timed out
    const results = await Promise.allSettled([
      propertyPhotosPromise,
      aerialMapViewUriPromise,
      streetMapViewUriPromise,
      userPhotosPromise,
    ]);

    // Create an array using only resolved Promises
    const resolvedPhotos = results.filter(r => r.status === 'fulfilled').flatMap(r => r.value);
    const propertyPhotoList = compact(resolvedPhotos);
    propertyUpdatePayload.propertyPhotoList = propertyPhotoList;

    // Update the property state and notify Redux to stop the fetching state
    await dispatch({ type: resetActionType, payload: propertyUpdatePayload });
    if (reportType !== 'soi' && !isComparable) {
      await dispatch(toggleFloorPlan(reportType, propertyUpdatePayload.floorPlanUris));
    }
    await dispatch(actions.updateFetching({propertyId, fetching: fetchingEndState}));
    return propertyPhotoList;
  };
}

export function fetchPropertyNotes({propertyId, comparableType}) {
  const {actions} = comparableActions[comparableType];
  return async (dispatch, getState) => {
    await dispatch(actions.updateFetching({propertyId, fetching: {propertyNotes: true}}));
    const note = await PropertyDetailsApi.getPropertyNotes(propertyId)
      .finally(() => dispatch(actions.updateFetching({propertyId, fetching: {propertyNotes: false}})));
    await dispatch(setPropertyNotes(propertyId, note));
    return note;
  };
}

export function fetchPropertyAvm({propertyId}) {
  const updateFields = PROPERTY_AVM_FIELDS;
  const startFetchState = updateFields.reduce((acc, field) => ({...acc, [field]: true}), {});
  const endFetchState = updateFields.reduce((acc, field) => ({...acc, [field]: false}), {});
  return async (dispatch, getState) => {
    const state = getState();
    const {reportType: {value}} = state;

    await dispatch(propertyDetailActions.updateFetching({propertyId, fetching: startFetchState}));
    const isRental = 'rental' === value;
    const field = isRental ? 'rentalAvm' : 'intelliValAvm';
    const promise = () => {
      return isRental ? PropertyDetailsApi.getPropertyRentalAvm(propertyId)
        : PropertyDetailsApi.getPropertyIntellivalAvm(propertyId);
    };
    const avm = await promise()
      .finally(() => dispatch(propertyDetailActions.updateFetching({propertyId, fetching: endFetchState})));
    await dispatch({type: PROPERTYDETAIL_UPDATE, payload: {propertyId, [field]: avm}});
    return avm;
  };
}

export function fetchPropertyTimeline({propertyId}) {
  const updateFields = PROPERTY_HISTORY_FIELDS;
  const startFetchState = updateFields.reduce((acc, field) => ({...acc, [field]: true}), {});
  const endFetchState = updateFields.reduce((acc, field) => ({...acc, [field]: false}), {});
  return async (dispatch, getState) => {
    await dispatch(propertyDetailActions.updateFetching({propertyId, fetching: startFetchState}));
    const history = await PropertyDetailsApi.getPropertyTimeline(propertyId)
      .finally(() => dispatch(propertyDetailActions.updateFetching({propertyId, fetching: endFetchState})));
    await dispatch({type: PROPERTYDETAIL_UPDATE, payload: {history}});
    return history;
  };
}

/**
 * @todo This kind of logic should really be handled in the Target Property/Summary components themselves
 */
function prepareTargetPropertyImages(res, propertyDetail) {
  let selectedImages = propertyDetail.selectedImages || [];
  let targetPhotoList = mapPlaceholderImages(propertyDetail.targetPhotoList, res.propertyPhotoList);
  res.selectedImages = selectedImages;
  res.targetPhotoList = targetPhotoList;
  return {
    selectedImages: PropertyDetailImages.summaryPropertyPhotoList(res),
    targetPhotoList: PropertyDetailImages.targetPropertyPhotoList(res),
  };
}

/**
 * Preserve the ordering of the array, loading in property photos in place of Placeholder values
 * @param images
 * @param propertyPhotoList
 * @returns {(*)[]}
 */
function mapPlaceholderImages(images, propertyPhotoList) {
  const photosCopy = [...propertyPhotoList];
  const mapper = img =>
    !img || img === PlaceholderImage
      ? photosCopy.splice(0, 1)[0] ?? PlaceholderImage
      : img;
  return images.map(mapper).concat(photosCopy);
}
